// Reusable "Are you sure?" confirm dialog. EVERY destructive action in the app
// routes through this (global rule, v0.5): removing a pending photo, removing a
// saved photo, deleting a booth.
//
// The guard rail is a deliberate 2-second delay: the YES/confirm button is
// disabled and counts down ("Yes (2)" → "Yes (1)" → "Yes") before it enables, so
// a fat-fingered tap can't destroy data. NO/cancel is active immediately, and a
// backdrop tap also cancels. Dependency-free, offline-safe, theme-aware (all
// colours are CSS tokens; verified Light + Dark).

export interface ConfirmOptions {
  title: string;
  message?: string;
  /** Label for the confirm button (default "Yes"). The countdown suffixes it. */
  confirmLabel?: string;
  /** Label for the cancel button (default "No"). */
  cancelLabel?: string;
  /** Style the confirm button as destructive (red). Default true — this dialog
   *  only ever guards removals. */
  danger?: boolean;
  /** Seconds the confirm button stays disabled + counts down (default 3). */
  delaySeconds?: number;
}

/** Show the dialog. Resolves true if confirmed, false if cancelled/dismissed. */
export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  const {
    title,
    message = '',
    confirmLabel = 'Yes',
    cancelLabel = 'No',
    danger = true,
    delaySeconds = 3,
  } = opts;

  return new Promise<boolean>((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'confirm';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <div class="confirm__box" role="document">
        <h2 class="confirm__title">${esc(title)}</h2>
        ${message ? `<p class="confirm__msg">${esc(message)}</p>` : ''}
        <div class="confirm__actions">
          <button type="button" class="btn confirm__no">${esc(cancelLabel)}</button>
          <button type="button" class="btn confirm__yes${danger ? ' confirm__yes--danger' : ''}" disabled>${esc(confirmLabel)}</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    // Lock background scroll while the dialog is up.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const yes = overlay.querySelector<HTMLButtonElement>('.confirm__yes')!;
    const no = overlay.querySelector<HTMLButtonElement>('.confirm__no')!;

    let remaining = Math.max(0, Math.round(delaySeconds));
    let timer: ReturnType<typeof setInterval> | null = null;

    const renderCountdown = (): void => {
      yes.textContent = remaining > 0 ? `${confirmLabel} (${remaining})` : confirmLabel;
    };

    if (remaining > 0) {
      renderCountdown();
      timer = setInterval(() => {
        remaining -= 1;
        renderCountdown();
        if (remaining <= 0) {
          if (timer) clearInterval(timer);
          timer = null;
          yes.disabled = false;
        }
      }, 1000);
    } else {
      yes.disabled = false;
    }

    // Focus NO first so the safe action is the default keyboard target.
    no.focus();

    const cleanup = (): void => {
      if (timer) clearInterval(timer);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      overlay.remove();
    };
    const settle = (value: boolean): void => {
      cleanup();
      resolve(value);
    };

    yes.addEventListener('click', () => {
      if (!yes.disabled) settle(true);
    });
    no.addEventListener('click', () => settle(false));
    // Backdrop tap cancels; a tap inside the box does not.
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) settle(false);
    });

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') settle(false);
      else if (e.key === 'Enter' && !yes.disabled) settle(true);
    };
    window.addEventListener('keydown', onKey);
  });
}

export interface PromptOptions {
  title: string;
  message?: string;
  placeholder?: string;
  /** Label for the confirm button (default "Add"). */
  confirmLabel?: string;
  /** Label for the cancel button (default "Cancel"). */
  cancelLabel?: string;
  /** Prefill the input with this value. */
  value?: string;
}

/**
 * Styled single-line prompt — the app's own dialog instead of `window.prompt`,
 * so the controlled "+ Add show…" flow matches the rest of the UI. Resolves the
 * entered string, or `null` on cancel/backdrop/Esc. No countdown (this isn't a
 * destructive action): the confirm button enables as soon as the field is
 * non-blank. Shares the confirm dialog's box + tokens (theme-aware, Light + Dark).
 * Validation of the returned value (trim / non-empty / de-dup) stays with the
 * caller.
 */
export function promptDialog(opts: PromptOptions): Promise<string | null> {
  const {
    title,
    message = '',
    placeholder = '',
    confirmLabel = 'Add',
    cancelLabel = 'Cancel',
    value = '',
  } = opts;

  return new Promise<string | null>((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'confirm';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <div class="confirm__box" role="document">
        <h2 class="confirm__title">${esc(title)}</h2>
        ${message ? `<p class="confirm__msg">${esc(message)}</p>` : ''}
        <input type="text" class="control confirm__input" placeholder="${esc(placeholder)}"
               value="${esc(value)}" autocapitalize="words" autocomplete="off" />
        <div class="confirm__actions">
          <button type="button" class="btn confirm__no">${esc(cancelLabel)}</button>
          <button type="button" class="btn btn--primary confirm__yes" disabled>${esc(confirmLabel)}</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const input = overlay.querySelector<HTMLInputElement>('.confirm__input')!;
    const yes = overlay.querySelector<HTMLButtonElement>('.confirm__yes')!;
    const no = overlay.querySelector<HTMLButtonElement>('.confirm__no')!;

    // Enable confirm only when there's non-blank text (caller still validates).
    const syncEnabled = (): void => {
      yes.disabled = input.value.trim().length === 0;
    };
    syncEnabled();
    input.addEventListener('input', syncEnabled);

    // Autofocus the field (and place the caret at the end) so typing starts here.
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);

    const cleanup = (): void => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      overlay.remove();
    };
    const settle = (result: string | null): void => {
      cleanup();
      resolve(result);
    };

    yes.addEventListener('click', () => {
      if (!yes.disabled) settle(input.value);
    });
    no.addEventListener('click', () => settle(null));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) settle(null);
    });

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') settle(null);
      else if (e.key === 'Enter' && !yes.disabled) {
        e.preventDefault();
        settle(input.value);
      }
    };
    window.addEventListener('keydown', onKey);
  });
}

export interface ChoiceOption<T> {
  value: T;
  label: string;
  /** Optional second line (e.g. "captured · revisit"). */
  hint?: string;
}

export interface ChooseOptions<T> {
  title: string;
  message?: string;
  options: ChoiceOption<T>[];
  cancelLabel?: string;
}

/**
 * Styled "pick one" dialog (v0.7) — the exhibitor directory's booth picker
 * ("which booth are you at?") and duplicate picker ("N captures — open which?").
 * Resolves the chosen value, or `null` on cancel/backdrop/Esc.
 *
 * Not destructive, so no countdown: options are live immediately. Shares the
 * confirm box + tokens, so it's theme-aware in Light and Dark like the other two.
 */
export function chooseDialog<T>(opts: ChooseOptions<T>): Promise<T | null> {
  const { title, message = '', options, cancelLabel = 'Cancel' } = opts;

  return new Promise<T | null>((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'confirm';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <div class="confirm__box" role="document">
        <h2 class="confirm__title">${esc(title)}</h2>
        ${message ? `<p class="confirm__msg">${esc(message)}</p>` : ''}
        <div class="choose__list">
          ${options
            .map(
              (o, i) => `
            <button type="button" class="choose__opt" data-i="${i}">
              <span class="choose__opt-label">${esc(o.label)}</span>
              ${o.hint ? `<span class="choose__opt-hint">${esc(o.hint)}</span>` : ''}
            </button>`,
            )
            .join('')}
        </div>
        <div class="confirm__actions">
          <button type="button" class="btn confirm__no">${esc(cancelLabel)}</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const cleanup = (): void => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      overlay.remove();
    };
    const settle = (value: T | null): void => {
      cleanup();
      resolve(value);
    };

    overlay.querySelectorAll<HTMLButtonElement>('.choose__opt').forEach((btn) => {
      btn.addEventListener('click', () => settle(options[Number(btn.dataset.i)].value));
    });
    overlay.querySelector<HTMLButtonElement>('.confirm__no')!.addEventListener('click', () => settle(null));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) settle(null);
    });

    overlay.querySelector<HTMLButtonElement>('.choose__opt')?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') settle(null);
    };
    window.addEventListener('keydown', onKey);
  });
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
